import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  pollBackfillIngest,
  pollBackfillPreview,
  reportFeedIssue,
  startBackfillIngest,
  startBackfillPreview,
} from '../../api/contentCompletion';
import BackfillReviewTable, { defaultSelectedUrls, isIngestiblePreviewItem } from './BackfillReviewTable';
import {
  BackfillFeedType,
  BackfillIngestResult,
  BackfillPreviewResult,
  CompanyContentHealth,
} from '../../types/contentCompletion';
import { useNotification } from '../../context/NotificationContext';
import { useEnvironment } from '../../hooks/useEnvironment';
import { resolveSourceCandidates } from './backfillUtils';

interface BackfillPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  company: CompanyContentHealth;
  defaultSourceUrl: string;
  onComplete?: () => void;
  onIssueReported?: () => void;
  /** Preloaded preview from batch backfill — skips re-running preview. */
  initialPreviewResult?: BackfillPreviewResult | null;
  initialDaysBack?: number;
  initialFeedType?: BackfillFeedType;
  /** Hide preview controls; user only reviews URLs and saves. */
  reviewOnly?: boolean;
}

const BackfillPreviewDialog: React.FC<BackfillPreviewDialogProps> = ({
  open,
  onClose,
  company,
  defaultSourceUrl,
  onComplete,
  onIssueReported,
  initialPreviewResult = null,
  initialDaysBack,
  initialFeedType,
  reviewOnly = false,
}) => {
  const { showNotification } = useNotification();
  const { environment } = useEnvironment();
  const [sourceUrl, setSourceUrl] = useState(defaultSourceUrl);
  const [feedType, setFeedType] = useState<BackfillFeedType>('auto');
  const [daysBack, setDaysBack] = useState(30);
  const [running, setRunning] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [result, setResult] = useState<BackfillPreviewResult | null>(null);
  const [ingestResult, setIngestResult] = useState<BackfillIngestResult | null>(null);
  const [confirmProductionOpen, setConfirmProductionOpen] = useState(false);
  const [productionConfirmed, setProductionConfirmed] = useState(false);
  const [skipSummaries, setSkipSummaries] = useState(false);
  const [skipLabelling, setSkipLabelling] = useState(false);
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());
  const stopPollRef = useRef<(() => void) | null>(null);
  const stopIngestPollRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (open) {
      setSourceUrl(defaultSourceUrl);
      setFeedType(initialFeedType ?? 'auto');
      setDaysBack(initialDaysBack ?? 30);
      setResult(initialPreviewResult ?? null);
      setIngestResult(null);
      setRunning(false);
      setIngesting(false);
      setConfirmProductionOpen(false);
      setProductionConfirmed(false);
      setSkipSummaries(false);
      setSkipLabelling(false);
      setSelectedUrls(new Set());
    }
    return () => {
      stopPollRef.current?.();
      stopIngestPollRef.current?.();
    };
  }, [open, defaultSourceUrl, initialPreviewResult, initialDaysBack, initialFeedType]);

  useEffect(() => {
    if (result?.status === 'success' && result.items.length > 0) {
      setSelectedUrls(defaultSelectedUrls(result.items));
    } else {
      setSelectedUrls(new Set());
    }
  }, [result]);

  const selectableItems =
    result?.items.filter(isIngestiblePreviewItem) ?? [];
  const selectedCount = selectableItems.filter((item) =>
    selectedUrls.has(item.url as string)
  ).length;
  const allSelectableSelected =
    selectableItems.length > 0 && selectedCount === selectableItems.length;

  const toggleUrl = (url: string) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) {
        next.delete(url);
      } else {
        next.add(url);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelectableSelected) {
      setSelectedUrls(new Set());
    } else {
      setSelectedUrls(new Set(selectableItems.map((item) => item.url as string)));
    }
  };

  const handlePreview = async () => {
    if (!sourceUrl.trim()) {
      showNotification('Source URL is required', 'error');
      return;
    }

    setRunning(true);
    setResult(null);
    setIngestResult(null);

    const candidates = resolveSourceCandidates(company);
    const startingUrl = sourceUrl.trim();
    // Only auto-fallback when previewing the preferred RSS (or first) source.
    const fallbackUrls =
      candidates[0] === startingUrl
        ? candidates.slice(1).filter((url) => url !== startingUrl)
        : [];
    const urlsToTry = [startingUrl, ...fallbackUrls];

    try {
      let lastUpdate: BackfillPreviewResult | null = null;

      for (let i = 0; i < urlsToTry.length; i += 1) {
        const url = urlsToTry[i];
        const isLast = i === urlsToTry.length - 1;
        setSourceUrl(url);

        const { run_id } = await startBackfillPreview({
          company_id: company.company_id,
          company_name: company.company_name,
          source_url: url,
          feed_type: feedType,
          days_back: daysBack,
          fetch_content: true,
        });

        const update = await new Promise<BackfillPreviewResult>((resolve) => {
          stopPollRef.current = pollBackfillPreview(run_id, (next) => {
            setResult(next);
            if (next.status !== 'running') {
              stopPollRef.current?.();
              resolve(next);
            }
          });
        });
        lastUpdate = update;

        if (update.status === 'success' && update.press_releases_found > 0) {
          showNotification(
            `Preview found ${update.press_releases_found} press release(s)`,
            'success'
          );
          setRunning(false);
          return;
        }

        if (!isLast) {
          // Prefer RSS first; if empty/failed, try press-release page before giving up.
          continue;
        }

        if (update.status === 'success') {
          showNotification(
            `Preview found ${update.press_releases_found} press release(s)`,
            'warning'
          );
        } else {
          showNotification(update.error || 'Preview failed', 'error');
        }
      }

      if (lastUpdate) setResult(lastUpdate);
      setRunning(false);
    } catch (err: unknown) {
      setRunning(false);
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to start preview';
      showNotification(msg, 'error');
    }
  };

  const runIngest = async () => {
    setIngesting(true);
    setIngestResult(null);

    try {
      const { run_id } = await startBackfillIngest({
        company_id: company.company_id,
        company_name: company.company_name,
        source_url: sourceUrl.trim(),
        feed_type: feedType,
        days_back: daysBack,
        fetch_content: true,
        skip_summaries: skipSummaries,
        skip_labelling: skipLabelling,
        preview_run_id: result?.run_id,
        selected_urls: Array.from(selectedUrls),
      });

      stopIngestPollRef.current = pollBackfillIngest(run_id, (update) => {
        setIngestResult(update);
        if (update.status !== 'running') {
          setIngesting(false);
          if (update.status === 'success') {
            showNotification(
              `Ingest complete: ${update.created} created, ${update.skipped_existing} skipped`,
              update.errors > 0 ? 'warning' : 'success'
            );
            onComplete?.();
          } else {
            showNotification(update.error || 'Ingest failed', 'error');
          }
        }
      });
    } catch (err: unknown) {
      setIngesting(false);
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to start ingest';
      showNotification(msg, 'error');
    }
  };

  const handleSaveToNeo4j = () => {
    if (environment === 'production') {
      setProductionConfirmed(false);
      setConfirmProductionOpen(true);
      return;
    }
    runIngest();
  };

  const handleConfirmProductionIngest = () => {
    if (!productionConfirmed) return;
    setConfirmProductionOpen(false);
    runIngest();
  };

  const handleReportIssue = async (issueType: string) => {
    try {
      await reportFeedIssue({
        company_id: company.company_id,
        company_name: company.company_name,
        source_url: sourceUrl.trim(),
        issue_type: issueType,
        days_back: daysBack,
        note: result?.error || undefined,
      });
      showNotification('Issue added to unreachable feed list', 'success');
      onIssueReported?.();
      onComplete?.();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to report issue';
      showNotification(msg, 'error');
    }
  };

  const handleClose = () => {
    stopPollRef.current?.();
    stopIngestPollRef.current?.();
    onClose();
  };

  const canIngest =
    result?.status === 'success' &&
    selectedCount > 0 &&
    !running &&
    !ingesting;

  const busy = running || ingesting;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {reviewOnly ? 'Review backfill' : 'Preview press release backfill'} — {company.company_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Source URL"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              fullWidth
              size="small"
              disabled={busy || reviewOnly}
              helperText="RSS feed or HTML press release listing page"
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 140 }} disabled={busy || reviewOnly}>
                <InputLabel>Feed type</InputLabel>
                <Select
                  value={feedType}
                  label="Feed type"
                  onChange={(e) => setFeedType(e.target.value as BackfillFeedType)}
                >
                  <MenuItem value="auto">Auto-detect</MenuItem>
                  <MenuItem value="rss">RSS</MenuItem>
                  <MenuItem value="html">HTML page</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Days back"
                type="number"
                size="small"
                value={daysBack}
                onChange={(e) => setDaysBack(Math.max(1, parseInt(e.target.value, 10) || 30))}
                inputProps={{ min: 1, max: 730 }}
                sx={{ width: 140 }}
                disabled={busy || reviewOnly}
              />
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!skipSummaries}
                    onChange={(e) => setSkipSummaries(!e.target.checked)}
                    disabled={busy}
                  />
                }
                label="Generate summaries"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!skipLabelling}
                    onChange={(e) => setSkipLabelling(!e.target.checked)}
                    disabled={busy}
                  />
                }
                label="Run content labelling"
              />
            </Box>

            {environment === 'production' && (
              <Alert severity="warning">
                You are in <strong>production</strong> mode. Saving will write to the live Neo4j
                database.
              </Alert>
            )}

            {running && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Fetching and extracting press releases…
                </Typography>
              </Box>
            )}

            {ingesting && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Saving selected press releases to Neo4j…
                </Typography>
              </Box>
            )}

            {result && result.status === 'success' && result.press_releases_found === 0 && (
              <Alert severity="warning">
                {result.latest_press_release_date
                  ? `No press releases found in the last ${result.days_back} day${
                      result.days_back === 1 ? '' : 's'
                    }. Latest on this source: ${result.latest_press_release_date}${
                      result.latest_press_release_title
                        ? ` — ${result.latest_press_release_title}`
                        : ''
                    }.`
                  : 'No press releases found for this URL and date range. The feed may be misconfigured or not fetchable.'}
              </Alert>
            )}

            {result && result.status === 'failure' && (
              <Alert severity="error">{result.error || 'Preview failed'}</Alert>
            )}

            {ingestResult && ingestResult.status === 'success' && (
              <Alert severity={ingestResult.errors > 0 ? 'warning' : 'success'}>
                Ingest complete: {ingestResult.created} article(s) created,{' '}
                {ingestResult.skipped_existing} skipped (already in Neo4j)
                {ingestResult.errors > 0 ? `, ${ingestResult.errors} error(s)` : ''}.
              </Alert>
            )}

            {ingestResult && ingestResult.status === 'failure' && (
              <Alert severity="error">{ingestResult.error || 'Ingest failed'}</Alert>
            )}

            {result && result.status === 'success' && result.items.length > 0 && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Found {result.press_releases_found} item(s), {result.content_fetched} with full
                  text · {selectedCount} selected for ingest (metadata-only rows save without body
                  text)
                  {result.feed_type_used ? ` · ${result.feed_type_used}` : ''}
                  {result.extraction_method ? ` · ${result.extraction_method}` : ''}
                </Typography>
                <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
                  <BackfillReviewTable
                    items={result.items}
                    selectedUrls={selectedUrls}
                    onToggleUrl={toggleUrl}
                    onToggleSelectAll={toggleSelectAll}
                    disabled={busy}
                    maxHeight="none"
                  />
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          {!reviewOnly &&
            result &&
            (result.press_releases_found === 0 || result.status === 'failure') && (
              <Button
                color="warning"
                onClick={() =>
                  handleReportIssue(
                    result.press_releases_found === 0 ? 'zero_results' : 'fetch_failed'
                  )
                }
                disabled={busy}
              >
                Report URL issue
              </Button>
            )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleClose} disabled={busy}>
            Close
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleSaveToNeo4j}
            disabled={!canIngest}
          >
            {ingesting ? 'Saving…' : `Save selected to Neo4j (${selectedCount})`}
          </Button>
          {!reviewOnly && (
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={busy || !sourceUrl.trim()}
            >
              {running ? 'Running…' : 'Run preview'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={confirmProductionOpen} onClose={() => setConfirmProductionOpen(false)}>
        <DialogTitle>Confirm production ingest</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You are about to write press releases for <strong>{company.company_name}</strong> to
            the <strong>production</strong> Neo4j database. This cannot be easily undone.
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={productionConfirmed}
                onChange={(e) => setProductionConfirmed(e.target.checked)}
              />
            }
            label="I understand this will modify production data"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmProductionOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmProductionIngest}
            disabled={!productionConfirmed}
          >
            Save to production
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BackfillPreviewDialog;
